import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Bloodtype as GlucoseIcon,
  MonitorWeight as WeightIcon,
  Favorite as BPIcon,
  HelpOutline as UnmatchedIcon,
} from "@mui/icons-material";
import { Layout } from "../components/Layout";
import {
  activePatientsService,
  ActivePatient,
} from "../services/activePatients.service";
import { clientService, Client } from "../services/client.service";

const ActivePatientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<number>(0);
  const [patients, setPatients] = useState<ActivePatient[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Filter state
  const [filterDiabetes, setFilterDiabetes] = useState(true);
  const [filterWeight, setFilterWeight] = useState(true);
  const [filterHypertension, setFilterHypertension] = useState(true);
  const [filterNoProgram, setFilterNoProgram] = useState(true);

  // Upload status toggle
  const [includeUploadStatus, setIncludeUploadStatus] = useState(false);

  // Sort state
  const [sortField, setSortField] = useState<
    "lastname" | "firstname" | "memberId"
  >("lastname");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const allClients = await clientService.getClients();
      // Filter to only active clients (statusID = 1)
      const activeClients = allClients.filter((c) => c.statusID === 1);
      setClients(activeClients);
    } catch (err) {
      setError("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleFetchPatients = async () => {
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const data = await activePatientsService.getActivePatients(
        selectedClient,
        includeUploadStatus,
      );
      setPatients(data);
      setSuccessMessage(`${data.length} active patients found`);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          "Failed to fetch active patients. Please try again.",
      );
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedClient) {
      setError("Please select a client");
      return;
    }

    setDownloading(true);
    setError("");

    try {
      const clientName =
        clients.find((c) => c.id === selectedClient)?.companyName ||
        `Client ${selectedClient}`;
      await activePatientsService.downloadActivePatientsFromData(
        patients,
        clientName,
      );
      setSuccessMessage("Excel file downloaded successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          "Failed to download Excel file. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  // Auto-fetch when client is selected or upload status toggle changes
  useEffect(() => {
    if (selectedClient && !loadingClients) {
      handleFetchPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, loadingClients, includeUploadStatus]);

  // Filter and sort patients (exclude unmatched from the main list)
  const getFilteredAndSortedPatients = () => {
    let filtered = patients.filter((patient) => {
      // Unmatched patients go to their own section
      if (patient.isUnmatched) return false;

      const hasNoProgram =
        !patient.diabetesProgram &&
        !patient.weightProgram &&
        !patient.hypertensionProgram;

      if (hasNoProgram) {
        return filterNoProgram;
      }

      // If all program filters are unchecked, hide all program patients
      if (!filterDiabetes && !filterWeight && !filterHypertension) {
        return false;
      }

      return (
        (filterDiabetes && patient.diabetesProgram) ||
        (filterWeight && patient.weightProgram) ||
        (filterHypertension && patient.hypertensionProgram)
      );
    });

    // Sort patients
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortField === "lastname") {
        comparison = a.lastname.localeCompare(b.lastname);
      } else if (sortField === "firstname") {
        comparison = a.firstname.localeCompare(b.firstname);
      } else if (sortField === "memberId") {
        comparison = a.memberId.localeCompare(b.memberId);
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  };

  const filteredPatients = getFilteredAndSortedPatients();

  // Unmatched CCM patients — sorted by last name
  const unmatchedPatients = patients
    .filter((p) => p.isUnmatched)
    .sort((a, b) => a.lastname.localeCompare(b.lastname));

  // Calculate program counts based on filtered patients
  const getProgramCounts = () => {
    const counts = {
      diabetes: 0,
      weight: 0,
      hypertension: 0,
      noProgram: 0,
    };

    filteredPatients.forEach((patient) => {
      if (patient.diabetesProgram) counts.diabetes++;
      if (patient.weightProgram) counts.weight++;
      if (patient.hypertensionProgram) counts.hypertension++;
      if (
        !patient.diabetesProgram &&
        !patient.weightProgram &&
        !patient.hypertensionProgram
      )
        counts.noProgram++;
    });

    return counts;
  };

  const programCounts = getProgramCounts();

  return (
    <Layout>
      <Box>
        <Typography variant='h4' gutterBottom>
          Active Patients Report
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          View active patients enrolled in CCM (partnering system) for selected
          client. Export data to Excel file with member details.
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity='success' sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {loadingClients ? (
          <Box display='flex' justifyContent='center' p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr auto auto" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <TextField
                  select
                  label='Select Client'
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(Number(e.target.value))}
                  fullWidth
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.companyName}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant='contained'
                  onClick={handleFetchPatients}
                  disabled={loading}
                  sx={{ height: 56 }}
                >
                  {loading ? <CircularProgress size={24} /> : "Refresh Data"}
                </Button>

                <Button
                  variant='contained'
                  color='success'
                  onClick={handleDownload}
                  disabled={downloading || patients.length === 0}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  sx={{ height: 56 }}
                >
                  {downloading ? "Generating..." : "Download Excel"}
                </Button>
              </Box>
            </Paper>

            {patients.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant='h6' gutterBottom>
                  Filter & Sort
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
                    gap: 3,
                    alignItems: "start",
                  }}
                >
                  {/* Program Filters */}
                  <Box>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Filter by Program
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterDiabetes}
                            onChange={(e) =>
                              setFilterDiabetes(e.target.checked)
                            }
                            sx={{
                              color: "#0393b8",
                              "&.Mui-checked": { color: "#0393b8" },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <GlucoseIcon
                              fontSize='small'
                              sx={{ color: "#0393b8" }}
                            />
                            <span>Diabetes</span>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterWeight}
                            onChange={(e) => setFilterWeight(e.target.checked)}
                            sx={{
                              color: "#569a45",
                              "&.Mui-checked": { color: "#569a45" },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <WeightIcon
                              fontSize='small'
                              sx={{ color: "#569a45" }}
                            />
                            <span>Weight</span>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterHypertension}
                            onChange={(e) =>
                              setFilterHypertension(e.target.checked)
                            }
                            sx={{
                              color: "#c73e2e",
                              "&.Mui-checked": { color: "#c73e2e" },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <BPIcon
                              fontSize='small'
                              sx={{ color: "#c73e2e" }}
                            />
                            <span>Hypertension</span>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterNoProgram}
                            onChange={(e) =>
                              setFilterNoProgram(e.target.checked)
                            }
                            sx={{
                              color: "#888",
                              "&.Mui-checked": { color: "#888" },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span style={{ color: "#888" }}>
                              No Program Assigned
                            </span>
                          </Box>
                        }
                      />
                    </FormGroup>

                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        gutterBottom
                      >
                        Additional Options
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeUploadStatus}
                            onChange={(e) =>
                              setIncludeUploadStatus(e.target.checked)
                            }
                          />
                        }
                        label='Include Upload Status'
                      />
                    </Box>
                  </Box>
                  <Box /> {/* Spacer */}
                  {/* Sort Options */}
                  <Box>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Sort By
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        select
                        size='small'
                        value={sortField}
                        onChange={(e) => setSortField(e.target.value as any)}
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value='lastname'>Last Name</MenuItem>
                        <MenuItem value='firstname'>First Name</MenuItem>
                        <MenuItem value='memberId'>Member ID</MenuItem>
                      </TextField>
                      <TextField
                        select
                        size='small'
                        value={sortDirection}
                        onChange={(e) =>
                          setSortDirection(e.target.value as any)
                        }
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value='asc'>Ascending</MenuItem>
                        <MenuItem value='desc'>Descending</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            )}

            {loading ? (
              <Box display='flex' justifyContent='center' p={4}>
                <CircularProgress />
              </Box>
            ) : patients.length > 0 ? (
              <Paper>
                <Box sx={{ p: 2, bgcolor: "grey.100" }}>
                  <Typography variant='h6'>
                    Active Patients ({filteredPatients.length}
                    {filteredPatients.length !== patients.length &&
                      ` of ${patients.length}`}
                    )
                  </Typography>
                  <Box
                    sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Chip
                        icon={<GlucoseIcon />}
                        label={`Diabetes: ${programCounts.diabetes}`}
                        size='small'
                        sx={{
                          bgcolor: "#0393b8",
                          color: "white",
                          "& .MuiChip-icon": { color: "white" },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Chip
                        icon={<WeightIcon />}
                        label={`Weight: ${programCounts.weight}`}
                        size='small'
                        sx={{
                          bgcolor: "#569a45",
                          color: "white",
                          "& .MuiChip-icon": { color: "white" },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Chip
                        icon={<BPIcon />}
                        label={`Hypertension: ${programCounts.hypertension}`}
                        size='small'
                        sx={{
                          bgcolor: "#c73e2e",
                          color: "white",
                          "& .MuiChip-icon": { color: "white" },
                        }}
                      />
                    </Box>
                    {programCounts.noProgram > 0 && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Chip
                          label={`No Program: ${programCounts.noProgram}`}
                          size='small'
                          sx={{
                            bgcolor: "#888",
                            color: "white",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Member ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>CCM ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Last Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>First Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Date of Birth</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Programs</strong>
                        </TableCell>
                        {includeUploadStatus && (
                          <>
                            <TableCell>
                              <strong>Uploading</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Last Upload</strong>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPatients.map((patient, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{patient.memberId}</TableCell>
                          <TableCell>
                            {patient.externalSystemId ? (
                              <Box
                                component='span'
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85rem",
                                  color: "text.secondary",
                                }}
                              >
                                {patient.externalSystemId}
                              </Box>
                            ) : (
                              <Box
                                component='span'
                                sx={{
                                  color: "text.disabled",
                                  fontStyle: "italic",
                                }}
                              >
                                -
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{patient.lastname}</TableCell>
                          <TableCell>{patient.firstname}</TableCell>
                          <TableCell>{patient.dob}</TableCell>
                          <TableCell>
                            <Box display='flex' gap={0.5}>
                              {patient.diabetesProgram && (
                                <Chip
                                  icon={<GlucoseIcon />}
                                  label='GL'
                                  size='small'
                                  sx={{
                                    bgcolor: "#0393b8",
                                    color: "white",
                                    "& .MuiChip-icon": { color: "white" },
                                  }}
                                />
                              )}
                              {patient.weightProgram && (
                                <Chip
                                  icon={<WeightIcon />}
                                  label='WS'
                                  size='small'
                                  sx={{
                                    bgcolor: "#569a45",
                                    color: "white",
                                    "& .MuiChip-icon": { color: "white" },
                                  }}
                                />
                              )}
                              {patient.hypertensionProgram && (
                                <Chip
                                  icon={<BPIcon />}
                                  label='BP'
                                  size='small'
                                  sx={{
                                    bgcolor: "#c73e2e",
                                    color: "white",
                                    "& .MuiChip-icon": { color: "white" },
                                  }}
                                />
                              )}
                              {!patient.diabetesProgram &&
                                !patient.weightProgram &&
                                !patient.hypertensionProgram &&
                                "-"}
                            </Box>
                          </TableCell>
                          {includeUploadStatus && (
                            <>
                              <TableCell>
                                {patient.isUploading ? (
                                  <Chip
                                    label='Yes'
                                    size='small'
                                    color='success'
                                    variant='outlined'
                                  />
                                ) : (
                                  <Chip
                                    label='No'
                                    size='small'
                                    color='default'
                                    variant='outlined'
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {patient.lastUploadDate ? (
                                  <Box
                                    component='span'
                                    sx={{
                                      fontSize: "0.85rem",
                                      color: "text.secondary",
                                    }}
                                  >
                                    {new Date(
                                      patient.lastUploadDate,
                                    ).toLocaleDateString()}
                                  </Box>
                                ) : (
                                  <Box
                                    component='span'
                                    sx={{
                                      color: "text.disabled",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    -
                                  </Box>
                                )}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <Paper sx={{ p: 4, textAlign: "center" }}>
                <Typography color='text.secondary'>
                  {selectedClient
                    ? "No active patients found for the selected client"
                    : "Please select a client to view active patients"}
                </Typography>
              </Paper>
            )}

            {/* Unmatched CCM patients section */}
            {unmatchedPatients.length > 0 && (
              <Paper sx={{ mt: 3, border: 1, borderColor: "warning.main" }}>
                <Box sx={{ p: 2, bgcolor: "warning.50" }}>
                  <Box display='flex' alignItems='center' gap={1}>
                    <UnmatchedIcon sx={{ color: "warning.main" }} />
                    <Typography variant='h6' sx={{ color: "warning.dark" }}>
                      CCM Patients Without TNS Match ({unmatchedPatients.length}
                      )
                    </Typography>
                  </Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.5 }}
                  >
                    These patients are active in CCM but no matching record was
                    found in the TNS system by name lookup. They may need to be
                    imported or their names may differ between systems.
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "warning.50" }}>
                        <TableCell>
                          <strong>CCM ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Member ID (CCM)</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Last Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>First Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Date of Birth</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmatchedPatients.map((patient, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box
                              component='span'
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                                color: "text.secondary",
                              }}
                            >
                              {patient.externalSystemId || "-"}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {patient.memberId || (
                              <Box
                                component='span'
                                sx={{
                                  color: "text.disabled",
                                  fontStyle: "italic",
                                }}
                              >
                                —
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{patient.lastname}</TableCell>
                          <TableCell>{patient.firstname}</TableCell>
                          <TableCell>{patient.dob}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Layout>
  );
};

export default ActivePatientsPage;
